import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogService,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { PreInscricao } from '../../core/api/pre-inscricao.dto';
import { PreInscricaoService } from '../../core/api/pre-inscricao.service';
import { BarraCarregandoComponent } from '../../shared/barra-carregando/barra-carregando.component';
import { ListaVaziaComponent } from '../../shared/lista-vazia/lista-vazia.component';
import { Ordenacao, OrdenavelComponent, paraParametroSort } from '../../shared/ordenavel/ordenavel.component';
import { mensagemDeErro } from '../../utils/mensagem-erro';
import { DialogAlertComponent } from '../pessoa-turma/turma-pessoa.dialog.component';
import { PreInscricaoFormComponent } from './pre-inscricao-form/pre-inscricao-form.component';
import { semHorarioComLugar } from './pre-inscricao-agendas/agenda-disponibilidade';
import { PreInscricaoAgendasDialogComponent } from './pre-inscricao-agendas/pre-inscricao-agendas.dialog';
import { PreInscricaoGruposDialogComponent } from './pre-inscricao-grupos/pre-inscricao-grupos.dialog';
import { PreInscricaoInscritosDialogComponent } from './pre-inscricao-inscritos/pre-inscricao-inscritos.dialog';

/**
 * Campanhas de pré-inscrição (gestão).
 *
 * Pontos de chamada (contrato igreja-api):
 * - `GET    /pre-inscricoes?page&size&filtro&sort`
 * - `PUT    /pre-inscricoes/{id}`  (encerrar e reabrir: só o campo `ativa` muda)
 * - `DELETE /pre-inscricoes/{id}`
 */
@Component({
  selector: 'app-pre-inscricao',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbFormFieldModule,
    NbIconModule,
    NbInputModule,
    NbSpinnerModule,
    ListaVaziaComponent,
    BarraCarregandoComponent,
    OrdenavelComponent,
  ],
  templateUrl: './pre-inscricao.component.html',
  styleUrl: './pre-inscricao.component.scss',
})
export class PreInscricaoComponent implements OnInit {
  campanhas: PreInscricao[] = [];
  page = 1;
  size = 10;
  totalPages = 0;
  carregando = false;
  filtro = '';
  salvandoId: string | null = null;

  /**
   * `PRE-15` — campanhas no ar sem horário futuro com lugar, que recusam toda pré-inscrição. A
   * listagem não traz os horários, então eles são lidos por campanha da página, só das que
   * estão no ar. Falha na leitura não marca a campanha: não se afirma o que não se sabe.
   */
  semHorario = new Set<string>();

  /** `TAB-01` — ordenação no servidor, porque a listagem é paginada pela API. */
  ordenacao: Ordenacao | null = { campo: 'nome', direcao: 'asc' };

  constructor(
    private service: PreInscricaoService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  protected get sort(): string | null {
    return paraParametroSort(this.ordenacao);
  }

  get temFiltro(): boolean {
    return this.filtro.trim() !== '';
  }

  ordenarPor(o: Ordenacao): void {
    this.ordenacao = o;
    this.page = 1;
    this.carregar();
  }

  /** `FIL-01` — o filtro vale quando a pessoa manda: Enter ou botão, nunca ao digitar. */
  buscarAgora(): void {
    this.page = 1;
    this.carregar();
  }

  limparFiltro(): void {
    this.filtro = '';
    this.buscarAgora();
  }

  onPageChange(p: number): void {
    if (p < 1 || (this.totalPages && p > this.totalPages)) return;
    this.page = p;
    this.carregar();
  }

  carregar(): void {
    this.carregando = true;
    this.service
      .listar(this.page - 1, this.size, this.filtro, this.sort)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (pagina) => {
          this.campanhas = pagina.content ?? [];
          this.totalPages = pagina.totalPages ?? 0;
          this.conferirHorarios();
        },
        error: (err) => {
          this.campanhas = [];
          this.totalPages = 0;
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível carregar as campanhas.'), 'Erro');
        },
      });
  }

  abrirDialog(): void {
    this.dialogService.open(PreInscricaoFormComponent).onClose.subscribe((salvou) => {
      if (salvou) this.carregar();
    });
  }

  editar(c: PreInscricao): void {
    this.dialogService
      .open(PreInscricaoFormComponent, { context: { campanha: c } })
      .onClose.subscribe((salvou) => {
        if (salvou) this.carregar();
      });
  }

  abrirGrupos(c: PreInscricao): void {
    if (!c.id) return;
    this.dialogService
      .open(PreInscricaoGruposDialogComponent, {
        context: { campanhaId: c.id, campanhaNome: c.nome },
      })
      .onClose.subscribe(() => this.carregar());
  }

  abrirAgendas(c: PreInscricao): void {
    if (!c.id) return;
    this.dialogService
      .open(PreInscricaoAgendasDialogComponent, {
        context: { campanhaId: c.id, campanhaNome: c.nome },
      })
      .onClose.subscribe(() => this.carregar());
  }

  private conferirHorarios(): void {
    const noAr = this.campanhas.filter((c) => c.ativa && c.id);
    if (!noAr.length) {
      this.semHorario = new Set();
      return;
    }
    forkJoin(
      noAr.map((c) =>
        this.service.listarAgendas(c.id!).pipe(
          map((agendas) => (semHorarioComLugar(agendas ?? []) ? c.id! : null)),
          catchError(() => of(null)),
        ),
      ),
    ).subscribe((ids) => (this.semHorario = new Set(ids.filter((id): id is string => !!id))));
  }

  abrirInscritos(c: PreInscricao): void {
    if (!c.id) return;
    this.dialogService.open(PreInscricaoInscritosDialogComponent, {
      context: { campanhaId: c.id, campanhaNome: c.nome },
    });
  }

  /**
   * Encerrar é `PUT ativa=false`, não exclusão: a API recusa excluir campanha com inscrito, e
   * o histórico de quem se pré-inscreveu não deve sumir porque o período acabou.
   */
  alternarAtiva(c: PreInscricao): void {
    if (!c.id || !c.nome || !c.dataReferenciaIdade) return;
    const ativa = !c.ativa;
    this.salvandoId = c.id;
    this.service
      .atualizar(c.id, {
        id: c.id,
        nome: c.nome,
        dataReferenciaIdade: c.dataReferenciaIdade,
        ativa,
      })
      .pipe(finalize(() => (this.salvandoId = null)))
      .subscribe({
        next: () => {
          this.toastr.success(
            ativa ? `${c.nome} está no ar de novo.` : `${c.nome} foi encerrada — o link público recusa novos envios.`,
            'Pré-inscrição',
          );
          this.carregar();
        },
        error: (err) =>
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível alterar a campanha.'), 'Erro'),
      });
  }

  excluir(c: PreInscricao): void {
    if (!c.id) return;
    const temInscrito = (c.totalInscritos ?? 0) > 0;
    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: 'Excluir campanha',
          message: temInscrito
            ? `${c.nome} tem ${c.totalInscritos} pré-inscrito(s). A API recusa excluir campanha com inscrito — para tirar do ar, use Encerrar.`
            : `Confirma a exclusão de ${c.nome}? Os grupos dela vão junto.`,
        },
      })
      .onClose.subscribe((confirmou) => {
        if (!confirmou || temInscrito) return;
        this.service.excluir(c.id!).subscribe({
          next: () => {
            this.toastr.success(`${c.nome} excluída.`, 'Pré-inscrição');
            this.carregar();
          },
          error: (err) =>
            this.toastr.danger(mensagemDeErro(err, 'Não foi possível excluir a campanha.'), 'Erro'),
        });
      });
  }

  /** O link que vai para as famílias. A rota pública não exige sessão. */
  linkPublico(c: PreInscricao): string {
    return `${window.location.origin}/pre-inscricao/${c.id}`;
  }

  copiarLink(c: PreInscricao): void {
    const link = this.linkPublico(c);
    navigator.clipboard?.writeText(link).then(
      () => this.toastr.success('Link da pré-inscrição copiado.', 'Link'),
      () => this.toastr.warning(link, 'Copie o link manualmente'),
    );
  }

  ocupacao(c: PreInscricao): string {
    const vagas = c.totalVagas ?? 0;
    const inscritos = c.totalInscritos ?? 0;
    return vagas ? `${inscritos} / ${vagas}` : `${inscritos} / —`;
  }
}
